import type {
  CfAccessIdentity,
  Ingredient,
  PaginatedResponse,
  Product,
  Store,
  Supplier,
} from "./types";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let detail = text;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail || `HTTP ${res.status}`);
  }

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== ""
  );
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const api = {
  // Products
  listProducts: (params: {
    search?: string;
    category?: string;
    status?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<PaginatedResponse<Product>> =>
    request(`/products${qs(params)}`),

  getProduct: (id: number): Promise<Product> =>
    request(`/products/${id}`),

  createProduct: (data: Partial<Product>): Promise<Product> =>
    request("/products", { method: "POST", body: JSON.stringify(data) }),

  updateProduct: (id: number, data: Partial<Product>): Promise<Product> =>
    request(`/products/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  deleteProduct: (id: number): Promise<Product> =>
    request(`/products/${id}`, { method: "DELETE" }),

  // Suppliers
  listSuppliers: (params: {
    search?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<PaginatedResponse<Supplier>> =>
    request(`/suppliers${qs(params)}`),

  createSupplier: (data: Partial<Supplier>): Promise<Supplier> =>
    request("/suppliers", { method: "POST", body: JSON.stringify(data) }),

  updateSupplier: (id: number, data: Partial<Supplier>): Promise<Supplier> =>
    request(`/suppliers/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  // Ingredients
  listIngredients: (params: {
    search?: string;
    category?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<PaginatedResponse<Ingredient>> =>
    request(`/ingredients${qs(params)}`),

  createIngredient: (data: Partial<Ingredient>): Promise<Ingredient> =>
    request("/ingredients", { method: "POST", body: JSON.stringify(data) }),

  updateIngredient: (id: number, data: Partial<Ingredient>): Promise<Ingredient> =>
    request(`/ingredients/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  // Stores
  listStores: (params: {
    search?: string;
    channel?: string;
    region?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<PaginatedResponse<Store>> =>
    request(`/stores${qs(params)}`),

  createStore: (data: Partial<Store>): Promise<Store> =>
    request("/stores", { method: "POST", body: JSON.stringify(data) }),

  updateStore: (id: number, data: Partial<Store>): Promise<Store> =>
    request(`/stores/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  // CF Access identity
  getIdentity: async (): Promise<CfAccessIdentity | null> => {
    try {
      const res = await fetch("/cdn-cgi/access/get-identity", {
        credentials: "include",
      });
      if (!res.ok) return null;
      return (await res.json()) as CfAccessIdentity;
    } catch {
      return null;
    }
  },
};
